import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, ClickAwayListener, Divider, IconButton, Popover, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { selectComments } from "../../redux/selectors/firebase";
import { Cell } from "../sheets/SheetContextMenu";
import CommentContainer, { Comments, ICommentContainer } from "./CommentContainer";

function filterCommentsByCell(sheetId: string, column: string, row: string)
{
    return ([id, commentContainer]: [string, ICommentContainer]) =>
    {
        return commentContainer.type.name === "sheet"
            && commentContainer.type.sheetId === sheetId
            && commentContainer.type.column === column
            && commentContainer.type.row === row
            && !commentContainer.deleted
            && !commentContainer.resolved;
    }
}

const useStyles = makeStyles((theme) => ({
    content: {
        width: "314px",
        maxHeight: "400px",
        overflowY: "auto",
        pointerEvents: "auto"
    },
    popover: {
        pointerEvents: "none"
    },
    commentNav: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center"
    },
    commentCounter: {
        marginLeft: "auto"
    }
}));

interface Props
{
    comments: Comments
    sheetId: string
    cell: Cell
    target: HTMLElement | null;
    onClose: () => void
}

function SheetComments({ comments, sheetId, cell, target, onClose }: Props)
{
    const classes = useStyles();
    const { column, row } = cell;
    const commentsList = useMemo(() =>
    {
        return Object.entries(comments ?? {}).filter(filterCommentsByCell(sheetId, column, row));
    }, [column, row, sheetId, comments]);

    const [commentIndex, setCommentIndex] = useState(0);

    useEffect(() =>
    {
        if (commentsList.length === 0)
        {
            onClose();
        }
        else if (commentIndex > commentsList.length - 1)
        {
            setCommentIndex(0);
        }
        else if (commentIndex < 0)
        {
            setCommentIndex(commentsList.length - 1);
        }
    }, [commentsList, commentIndex, onClose]);

    useEffect(() =>
    {
        setCommentIndex(0);
    }, [column, row, sheetId])

    const [id, commentDetails] = commentsList[commentIndex] ?? [null, null];

    const open = Boolean(target && document.body.contains(target));
    return <Popover open={open} classes={{ root: classes.popover }} disableEnforceFocus disableAutoFocus disableRestoreFocus anchorEl={target} onClose={onClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <ClickAwayListener onClickAway={onClose}>
            <Box className={classes.content}>
                {open && <Box>
                    {commentsList.length > 1 && <>
                        <Box className={classes.commentNav}>
                            <Typography component={"span"} className={classes.commentCounter} variant={"body2"}>{`${commentIndex + 1}/${commentsList.length}`}</Typography>
                            <IconButton onClick={() => setCommentIndex(commentIndex - 1)}><KeyboardArrowLeft /></IconButton>
                            <IconButton onClick={() => setCommentIndex(commentIndex + 1)}><KeyboardArrowRight /></IconButton>
                        </Box>
                        <Divider />
                    </>}
                    {id && commentDetails && <CommentContainer key={id} commentId={id} details={commentDetails} defaultSelected={true} component={Box} />}
                </Box>}
            </Box>
        </ClickAwayListener>
    </ Popover>
}

const mapStateToProps = (store: never) => ({
    comments: selectComments(store)
});

export default connect(mapStateToProps, {
})(SheetComments);